
import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
import { useIsMobile } from '@/hooks/use-mobile';

const ImmigrantsCounter = () => {
  const [count, setCount] = useState(5300);
  const [isSticky, setIsSticky] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);
  const isMobile = useIsMobile();

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      const heroSection = document.querySelector('header');
      if (heroSection) {
        const heroBottom = heroSection.getBoundingClientRect().bottom;
        setIsSticky(heroBottom < 0);
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const minInterval = 10000; // 10 seconds
    const maxInterval = 60000; // 1 minute
    const minPause = 3 * 60000; // 3 minutes
    const maxPause = 5 * 60000; // 5 minutes

    const getRandomDelay = () => Math.floor(Math.random() * (maxInterval - minInterval) + minInterval);
    const getRandomPause = () => Math.floor(Math.random() * (maxPause - minPause) + minPause);
    const getRandomIncrement = () => Math.floor(Math.random() * 2) + 1; // 1 or 2

    const incrementCounter = () => {
      const shouldPause = Math.random() < 0.3; // 30% chance to pause
      
      if (!shouldPause) {
        setCount(prev => {
          const newCount = prev + getRandomIncrement();
          // Store the count in localStorage to persist between page reloads
          localStorage.setItem('immigrantsCount', newCount.toString());
          return newCount;
        });
        setTimeout(incrementCounter, getRandomDelay());
      } else {
        setTimeout(incrementCounter, getRandomPause());
      }
    };

    // Initialize count from localStorage or use default
    const savedCount = localStorage.getItem('immigrantsCount');
    if (savedCount) {
      setCount(parseInt(savedCount, 10));
    }

    const timer = setTimeout(incrementCounter, getRandomDelay());
    return () => clearTimeout(timer);
  }, []);

  const formattedCount = count.toLocaleString('es-ES');

  const renderCounter = useCallback(() => (
    <div className="flex items-center justify-center w-full relative">
      <div className="text-center">
        <motion.div 
          className="text-sm font-medium text-blue-600"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: "easeOut" }}
        >
          Más De
        </motion.div>
        <motion.div 
          className="font-bold text-3xl md:text-4xl text-primary"
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ 
            scale: 1,
            opacity: 1,
            transition: {
              type: "spring",
              stiffness: 100,
              damping: 15
            }
          }}
        >
          {formattedCount}
        </motion.div>
        <motion.div 
          className="text-sm font-medium text-blue-600"
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: "easeOut" }}
        >
          Inmigrantes Ayudados
        </motion.div>
      </div>
    </div>
  ), [formattedCount]);

  const renderExpandedInfo = () => (
    <motion.div 
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 5 }}
      transition={{ duration: 0.3, ease: "easeInOut" }}
      className="text-sm mt-3 text-center text-blue-600 font-medium"
    >
      Miles de inmigrantes ya han recibido ayuda. ¡Únete ahora!
    </motion.div>
  );

  if (isDismissed && isSticky) return null;

  return (
    <motion.div
      initial={false}
      animate={{
        scale: isExpanded ? 1.02 : 1,
      }}
      transition={{
        scale: {
          duration: 0.4,
          ease: [0.4, 0, 0.2, 1]
        }
      }}
      whileHover={{ 
        scale: 1.05,
        transition: {
          duration: 0.2,
          ease: "easeOut"
        }
      }}
      whileTap={{ 
        scale: 0.98 
      }}
      className={`
        relative
        cursor-pointer
        ${isSticky 
          ? 'fixed bottom-4 right-4 z-50 bg-white rounded-lg sm:rounded-xl shadow-2xl p-3 sm:p-4 max-w-[280px]' 
          : 'bg-white rounded-lg sm:rounded-xl p-4 sm:p-6 mt-0 mb-12 md:mb-16 mx-auto max-w-[300px] z-10'}
        transition-all duration-300
        hover:shadow-[0_20px_40px_rgba(0,0,0,0.15)]
        active:scale-95
        group
        border border-blue-100
        bg-gradient-to-br from-white to-blue-50
      `}
      onMouseEnter={() => !isMobile && setIsExpanded(true)}
      onMouseLeave={() => !isMobile && setIsExpanded(false)}
      onClick={() => isMobile && setIsExpanded(!isExpanded)}
    >
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ 
          opacity: [0, 0.3, 0],
        }}
        transition={{
          duration: 2,
          repeat: Infinity,
          ease: "easeInOut"
        }}
        className="absolute inset-0 bg-gradient-to-br from-blue-400/20 to-purple-400/20 rounded-lg sm:rounded-xl"
      />
      {isSticky && (
        <motion.button
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          transition={{
            type: "spring",
            stiffness: 400,
            damping: 25
          }}
          onClick={e => {
            e.stopPropagation();
            setIsDismissed(true);
          }}
          className="absolute -top-2 -right-2 bg-white text-primary rounded-full p-1 shadow-lg hover:shadow-xl transition-all duration-300"
        >
          <X size={14} className="sm:w-4 sm:h-4" />
        </motion.button>
      )}
      {renderCounter()}
      <AnimatePresence>
        {isExpanded && renderExpandedInfo()}
      </AnimatePresence>
    </motion.div>
  );
};

export default ImmigrantsCounter;
